// Simple API URL helper
// Use this to fix API paths for production deployment

export const getApiUrl = (path) => {
  // Use full URL for production, relative path for development
  return process.env.NODE_ENV === 'production' 
    // ? `https://demo.aifwall.ai${path}`
    ? `https://demo.whitehax.com${path}`
    : path;
};

/** True when report was produced by the consolidate API / Consolidated Report flow. */
export const isConsolidatedReport = (reportData) =>
  Boolean(reportData?.is_consolidated);

/** Account page Jobs API toggle (persisted). */
export const JOBS_API_TARGET_KEY = 'aif_jobs_api_target';
export const CLOUD_SERVER_BASE_URL_KEY = 'aif_cloud_server_base_url';
export const RUNNER_TOKEN_KEY = 'aif_runner_token';

export function getLocalRunnerBase() {
  const fromEnv = (process.env.REACT_APP_LOCAL_RUNNER_URL || '').trim();
  return (fromEnv || 'http://127.0.0.1:8000').replace(/\/$/, '');
}

/**
 * @returns {'localhost' | 'server' | 'cloudserver'}
 */
export function readJobsApiTarget() {
  if (typeof window === 'undefined') return 'server';
  try {
    const v = localStorage.getItem(JOBS_API_TARGET_KEY);
    if (v === 'localhost') return 'localhost';
    if (v === 'cloudserver') return 'cloudserver';
  } catch (_) {}
  return 'server';
}

export function writeJobsApiTarget(value) {
  const next =
    value === 'localhost'
      ? 'localhost'
      : value === 'cloudserver'
        ? 'cloudserver'
        : 'server';
  try {
    localStorage.setItem(JOBS_API_TARGET_KEY, next);
  } catch (_) {}
}

/** Persisted Cloud Server base URL (no trailing slash). */
export function readCloudServerBaseUrl() {
  if (typeof window === 'undefined') return '';
  try {
    return String(localStorage.getItem(CLOUD_SERVER_BASE_URL_KEY) || '').trim().replace(/\/$/, '');
  } catch (_) {
    return '';
  }
}

export function writeCloudServerBaseUrl(value) {
  const next = String(value || '').trim().replace(/\/$/, '');
  try {
    if (next) localStorage.setItem(CLOUD_SERVER_BASE_URL_KEY, next);
    else localStorage.removeItem(CLOUD_SERVER_BASE_URL_KEY);
  } catch (_) {}
}

/** Same as readCloudServerBaseUrl — use wherever jobs need the cloud base. */
export function getCloudServerBase() {
  return readCloudServerBaseUrl();
}

/**
 * Jobs API URL.
 * - localhost → local runner
 * - cloudserver → saved Cloud Server base URL
 * - server → prod (getApiUrl)
 * List/schedule use getProdJobsUrl (prod, or cloud when cloudserver is selected).
 * @param {string} [pathSuffix] - e.g. '' for create job, 'schedule/' for schedule
 * @param {{ force?: 'localhost' | 'server' | 'cloudserver' }} [opts]
 */
export function getJobsUrl(pathSuffix = '', opts = {}) {
  const target = opts.force || readJobsApiTarget();
  const suffix = String(pathSuffix || '').replace(/^\//, '');
  if (target === 'localhost') {
    return `${getLocalRunnerBase()}/api/internal/jobs/${suffix}`;
  }
  if (target === 'cloudserver') {
    const base = getCloudServerBase();
    if (!base) {
      throw new Error('Cloud Server Base URL is not set. Save it on the Account page.');
    }
    return `${base}/api/internal/jobs/${suffix}`;
  }
  return getApiUrl(`/api/internal/jobs/${suffix}`);
}

/** Non-localhost jobs host: cloud base when cloudserver is selected, otherwise prod. */
export function getProdJobsUrl(pathSuffix = '') {
  if (readJobsApiTarget() === 'cloudserver') {
    return getJobsUrl(pathSuffix, { force: 'cloudserver' });
  }
  return getJobsUrl(pathSuffix, { force: 'server' });
}

export function readRunnerToken() {
  if (typeof window === 'undefined') return '';
  try {
    return sessionStorage.getItem(RUNNER_TOKEN_KEY) || '';
  } catch (_) {
    return '';
  }
}

export function writeRunnerToken(token) {
  try {
    if (token) sessionStorage.setItem(RUNNER_TOKEN_KEY, token);
    else sessionStorage.removeItem(RUNNER_TOKEN_KEY);
  } catch (_) {}
}

export async function fetchRunnerToken(forceRefresh = false) {
  if (!forceRefresh) {
    const cached = readRunnerToken();
    if (cached) return cached;
  }
  const csrfToken = await getCsrfToken();
  const response = await fetch(getApiUrl('/api/internal/runner/token/'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
    },
    body: '{}',
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.error || !data.token) {
    throw new Error(data.message || data.detail || 'Unable to issue local runner token');
  }
  writeRunnerToken(data.token);
  return data.token;
}

/**
 * Register a hybrid job on prod using the browser session (no runner token yet).
 */
export async function registerProdRunnerJob(formData) {
  const csrfToken = await getCsrfToken();
  const response = await fetch(getApiUrl('/api/internal/runner/jobs/'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRFToken': csrfToken },
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.error || !data.job_id) {
    throw new Error(data.message || data.detail || 'Unable to register job on server');
  }
  return data;
}

/**
 * Download the job's selected input files (and target scripts) from prod.
 */
export async function downloadProdJobBundle(jobId) {
  const response = await fetch(
    getApiUrl(`/api/internal/runner/jobs/${encodeURIComponent(jobId)}/bundle/`),
    {
      method: 'GET',
      credentials: 'include',
    }
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.detail || 'Unable to download selected test files');
  }
  return response.blob();
}

/**
 * Start WHFTCLI on the local hybrid runner after registering the job on prod.
 */
export async function startLocalHybridJob({ profileId, reportName, registered }) {
  const bundle = await downloadProdJobBundle(registered.job_id);
  const startData = new FormData();
  startData.append('profile_id', String(profileId));
  startData.append('job_id', String(registered.job_id));
  startData.append('user_id', String(registered.user_id));
  if (reportName) {
    startData.append('report_name', reportName);
  }
  startData.append('bundle', bundle, `job_${registered.job_id}_bundle.zip`);
  return localJobsFetch('', {
    method: 'POST',
    body: startData,
  });
}

/**
 * Fetch a local-runner job URL.
 * Bearer token is optional for now (localhost-only). Pass runnerToken later for prod.
 */
export async function localJobsFetch(pathSuffix, options = {}) {
  const { runnerToken, ...rest } = options;
  const headers = { ...(rest.headers || {}) };
  if (runnerToken) {
    headers.Authorization = `Bearer ${runnerToken}`;
  }
  return fetch(getJobsUrl(pathSuffix, { force: 'localhost' }), {
    ...rest,
    headers,
    credentials: 'omit',
  });
}

/**
 * Check license limits before starting test job(s).
 * @param {number} [requestedRuns=1] - Number of new jobs about to be started.
 * Throws if current_total_runs + requestedRuns > max_test_runs.
 */
export async function assertTestAllowed(requestedRuns = 1) {
  const response = await fetch(getApiUrl('/api/internal/license/isTestAllowed/'), {
    method: 'GET',
    credentials: 'include',
  });

  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok || data.success === false) {
    throw new Error(data.detail || 'Unable to verify test license limits');
  }

  const runs = Math.max(1, Number(requestedRuns) || 1);
  const current = Number(data.current_total_runs) || 0;
  const max = data.max_test_runs;

  // max_test_runs is null when the license has no finite cap
  if (max == null) {
    if (data.allowed === false) {
      throw new Error('You have reached the maximum allowed test runs for this license.');
    }
    return data;
  }

  if (current + runs > max) {
    const remaining = Math.max(0, max - current);
    if (runs > 1) {
      throw new Error(
        `You have reached the maximum allowed test runs for this license. This Quick Start would start ${runs} tests, but only ${remaining} of ${max} remain. Deselect some tests or upgrade your license.`
      );
    }
    throw new Error(
      `You have reached the maximum allowed test runs for this license. Upgrade your license to run more.`
    );
  }

  return data;
}

// CSRF token management
let csrfToken = null;
let csrfTokenPromise = null; // Prevent concurrent requests

/**
 * Fetch CSRF token from the server
 * @param {boolean} forceRefresh - Force refresh even if token exists
 * @returns {Promise<string>} CSRF token
 */
export const getCsrfToken = async (forceRefresh = false) => {
  // Return cached token if available and not forcing refresh
  if (csrfToken && !forceRefresh) {
    return csrfToken;
  }

  // If a request is already in progress, wait for it
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }

  // Create new request
  csrfTokenPromise = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(getApiUrl('/api/internal/auth/csrf/'), {
        method: 'GET',
        credentials: 'include', // Include cookies for session
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch CSRF token');
      }

      const data = await response.json();
      if (data.success && data.csrf_token) {
        // Validate token format (basic check)
        if (typeof data.csrf_token === 'string' && data.csrf_token.length > 0) {
          csrfToken = data.csrf_token;
          csrfTokenPromise = null;
          return csrfToken;
        }
        throw new Error('Invalid CSRF token format');
      }

      throw new Error('Invalid CSRF token response');
    } catch (error) {
      csrfTokenPromise = null;
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching CSRF token:', error);
      }
      throw error;
    }
  })();

  return csrfTokenPromise;
};

/**
 * Clear cached CSRF token (useful after logout or on 403 errors)
 */
export const clearCsrfToken = () => {
  csrfToken = null;
  csrfTokenPromise = null;
};

/**
 * Check if user is authenticated
 * @returns {Promise<{authenticated: boolean, user?: object, license?: object}>}
 */
export const checkAuth = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const response = await fetch(getApiUrl('/api/internal/auth/check/'), {
      method: 'GET',
      credentials: 'include', // Include cookies for session
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.status === 401) {
      return { authenticated: false };
    }

    if (!response.ok) {
      throw new Error('Failed to check authentication');
    }

    const data = await response.json();
    return {
      authenticated: data.authenticated || false,
      user: data.user || null,
      license: data.license || null,
    };
  } catch (error) {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error checking authentication:', error);
    }
    return { authenticated: false };
  }
};

/**
 * Get user ID from server-side session
 * 
 * Usage example:
 *   const userId = await getUserId();
 *   if (!userId) {
 *     throw new Error('User ID not found. Please log in again.');
 *   }
 *   const response = await fetch(`/api/internal/...?user_id=${userId}`);
 * 
 * @returns {Promise<number|null>} User ID or null if not authenticated
 */
let cachedUserId = null;
let userIdPromise = null;

export const getUserId = async () => {
  // Return cached user ID if available
  if (cachedUserId) {
    return cachedUserId;
  }

  // If a request is already in progress, wait for it
  if (userIdPromise) {
    return userIdPromise;
  }

  userIdPromise = (async () => {
    try {
      const authResult = await checkAuth();
      if (authResult.authenticated && authResult.user && authResult.user.id) {
        cachedUserId = authResult.user.id;
        userIdPromise = null;
        return cachedUserId;
      }
    } catch (error) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error getting user ID from session:', error);
      }
    }

    userIdPromise = null;
    return null;
  })();

  return userIdPromise;
};

/**
 * Clear cached user ID (useful after logout)
 */
export const clearUserId = () => {
  cachedUserId = null;
  userIdPromise = null;
};

/**
 * Get admin ID (same as user ID for admin users)
 * @returns {Promise<number|null>} Admin ID or null if not authenticated
 */
export const getAdminId = async () => {
  return getUserId(); // For now, admin_id is the same as user_id
};

/**
 * Make an authenticated API request with CSRF token
 * Automatically handles CSRF token refresh on 403 errors
 * @param {string} path - API path
 * @param {object} options - Fetch options
 * @param {number} retries - Number of retries for CSRF token refresh (default: 1)
 * @returns {Promise<Response>}
 */
export const authenticatedFetch = async (path, options = {}, retries = 1) => {
  try {
    const token = await getCsrfToken();
    
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': token,
      ...options.headers,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    const response = await fetch(getApiUrl(path), {
      ...options,
      headers,
      credentials: 'include', // Include cookies for session
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // If CSRF token is invalid (403), refresh and retry once
    if (response.status === 403 && retries > 0) {
      clearCsrfToken();
      return authenticatedFetch(path, options, retries - 1);
    }

    return response;
  } catch (error) {
    // Handle timeout and network errors
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
};
